import React from 'react';
import _ from 'lodash';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {withTheme} from 'styled-components';
import {Field, Form, FormValidation, getFormData, untouch} from '@nexios/frontend-forms';

import Box from '../Box';
import Button from '../Button';
import ButtonContainer from '../ButtonContainer';
import Dropdown from '../Dropdown';
import ErrorMessage from '../ErrorMessage';
import FieldWrapper from '../FieldWrapper';
import FormLayout from '../FormLayout';
import Input from '../Input';
import InputAmount from '../InputAmount';
import PaymentFailedMessage from '../PaymentFailedMessage';
import PaymentMethodButtonContainer from '../PaymentMethodButtonContainer';
import RadioButtonContainer from '../RadioButtonContainer';
import parseText from '../../utils/parseText';
import {submitThankYouAsk} from '../../reducer';
import createThankYouFormConfig from './createThankYouFormConfig';
import SocialShare from '../SocialShare';
import Title from '../Title';
import ProgressBar from '../ProgressBar';

const FORM_INSTANCE_KEY = 'thankYouAsk';

const ThankYouAskForm = ({askType, config, formContext, formData, themeName, thankYouAskState, submit, untouchAmountFields, history, children, theme, progressBar}) => {
  if (thankYouAskState === 'done') {
    return <Box>
      {theme?.title?.showOnThankYou && <Title title={formContext.step4Header}><ProgressBar progress={progressBar} /></Title>}
      <div className='widgetContent'>
        <h1>{config.thankYouTitle}</h1>
        <div className='field'>
          <div dangerouslySetInnerHTML={{__html: config.thankYouText}} />
        </div>

        <SocialShare formContext={formContext} />
      </div>
    </Box>;
  }

  if (!config || config.enabled !== true) {
    return children;
  }

  const askingRecurring = askType === 'recurring';

  if (askingRecurring && _.isEmpty(config.frequencies)) {
    return children;
  }

  const title = parseText(formData, config.title);
  const text = parseText({...formData, ...formContext}, config.textHtml_txt, themeName);

  const onSubmit = (hist, data) => {
    submit(askType, data, hist);
  };
  const formConfig = createThankYouFormConfig({askType, config, formContext, submit: onSubmit, untouchAmountFields, history, theme});

  return <Box>
    {theme?.title?.showOnThankYou && <Title title={formContext.step4Header}><ProgressBar progress={progressBar} /></Title>}
    <Form
      autoComplete='off'
      formInstanceKey={FORM_INSTANCE_KEY}
      formContext={formContext}
      formConfig={formConfig}>
      <PaymentFailedMessage formContext={formContext} />
      <FormValidation
        component={ErrorMessage}
        errorMessage='Je hebt nog niet alle velden ingevuld.' />
      <div className='widgetContent'>
        <FormLayout>
          {title && <h2>{title}</h2>}
          {text && <div className='field'><div dangerouslySetInnerHTML={{__html: text}} /></div>}

          {askingRecurring && <Field
            list='frequencies'
            name='frequency'
            component={(props) => {
              const list = _.get(props, ['list']);
              // No selector needed when only one frequency is offered.
              if (_.size(list) < 2 && config.frequencyDisplay === 'dropdown') {
                return null;
              }
              return config.frequencyDisplay === 'radiobutton'
                ? <RadioButtonContainer {...props} />
                : <Dropdown {...props} />;
            }}
          />}

          <Field
            list='amounts'
            name='amount_m'
            component={(props) => {
              const list = props.list.map(a => {
                a.ribbon = a.value.ribbon;
                a.icon = a.value.icon;
                a.fieldName = a.value.fieldName;
                a.value = a.value.value;
                return a;
              });

              return <ButtonContainer fieldName='amount_m' list={list} />
            }}
          />

          <Field
            name='setCustomAmountVisible'
            component={({updateField}) => <div className='centered'>
              <button type='button' className='link' onClick={() => {
                updateField('isCustomAmountVisible', true);
                updateField('amount_m', null);
              }}>Of kies een ander bedrag</button>
            </div>}
          />
          <Field
            label={config.customAmountDisplay === 'inline' ? null : 'Vul het bedrag in dat je wilt doneren'}
            name='customAmount_m'
            placeholder={config.customAmountDisplay === 'inline' ? 'Of vul hier een ander bedrag in' : '0,00'}
            component={FieldWrapper}
            inputComponent={InputAmount}
          />
          <Field
            name='setCustomAmountHidden'
            component={({updateField}) => <div className='centered'>
              <button type='button' className='link' onClick={() => {
                updateField('isCustomAmountVisible', false);
                updateField('customAmount_m', null);
              }}>Of kies bedrag</button>
            </div>}
          />

          <Field
            name='amountText'
            component={({value}) => (value ? <div className='field'>{value}</div> : null)}
          />

          {!askingRecurring && <Field
            name='paymentMethod'
            list='paymentMethods'
            component={(props) => <PaymentMethodButtonContainer fieldName='paymentMethod' list={props.list} />}
          />}
          {!askingRecurring && <Field
            list='idealBanks'
            name='bank'
            component={FieldWrapper}
            placeholder='Kies je bank'
            inputComponent={Dropdown}
          />}
          {!askingRecurring && <Field
            list='creditCardBanks'
            name='creditCardBank'
            component={FieldWrapper}
            placeholder='Kies je creditcard'
            inputComponent={Dropdown}
          />}
          <Field
            label='Rekeningnummer'
            name='iban'
            component={FieldWrapper}
            inputComponent={Input}
            placeholder='NL11ABNA012345678'
            forceUppercase />

          <FormValidation
            type='submit'
            label={config.buttonLabel || 'Doneer'}
            component={Button}
          />
        </FormLayout>
      </div>
    </Form>
  </Box>;
};

const mapStateToProps = (state) => ({
  formContext: state.reducer.formContext,
  formData: getFormData(state, 'donate'),
  themeName: state.reducer.theme,
  thankYouAskState: state.reducer.thankYouAskState
});

const mapDispatchToProps = (dispatch) => ({
  submit: (askType, data, history) => {
    dispatch(submitThankYouAsk(askType, data, history));
  },
  untouchAmountFields: () => {
    dispatch(untouch({instanceKey: FORM_INSTANCE_KEY, field: 'customAmount_m'}));
    dispatch(untouch({instanceKey: FORM_INSTANCE_KEY, field: 'amount_m'}));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(ThankYouAskForm)));
